import React, { useState, useRef } from 'react';
import { playSpookySound } from './spookyAudio';

const SPIDER_QUOTES = [
    'EEK! Don’t touch my web! 🕷️',
    'Careful, I spin byte-sized traps! 🕸️',
    'Boo! Looking for spooky discounts? 🎃',
    'I see you browsing... 👀',
    'Scuttling away into the dark! 🦇'
];

const DanglingSpider = () => {
    const [dropped, setDropped] = useState(false);
    const [scared, setScared] = useState(false);
    const [quote, setQuote] = useState('');
    const quoteTimeoutRef = useRef(null);
    const dropTimeoutRef = useRef(null);

    const triggerReaction = (isClick = false) => {
        playSpookySound('flutter');
        setScared(true);
        setDropped(true);

        if (isClick) {
            const randomQuote = SPIDER_QUOTES[Math.floor(Math.random() * SPIDER_QUOTES.length)];
            setQuote(randomQuote);
            if (quoteTimeoutRef.current) clearTimeout(quoteTimeoutRef.current);
            quoteTimeoutRef.current = setTimeout(() => setQuote(''), 2200);
        }

        if (dropTimeoutRef.current) clearTimeout(dropTimeoutRef.current);
        dropTimeoutRef.current = setTimeout(() => {
            setDropped(false);
            setScared(false);
        }, 1800);
    };

    return (
        /* Hidden on mobile to prevent any collision with mobile search, buttons or categories */
        <div className="hidden md:block fixed top-0 right-10 lg:right-28 z-[20] pointer-events-none select-none">
            {/* Speech bubble */}
            {quote && (
                <div className="absolute right-0 top-16 bg-gray-900/95 text-orange-400 border border-orange-500/50 text-xs font-semibold px-3 py-1.5 rounded-xl shadow-xl shadow-orange-950/50 w-48 text-center whitespace-normal break-words animate-bounce pointer-events-none">
                    {quote}
                    <div className="absolute -top-1 right-5 w-2 h-2 bg-gray-900 border-t border-l border-orange-500/50 rotate-45" />
                </div>
            )}

            <div
                className={`relative flex flex-col items-center transition-all duration-700 ease-out pointer-events-auto cursor-pointer ${
                    dropped ? 'translate-y-12' : 'translate-y-0'
                }`}
                style={{ transformOrigin: 'top center' }}
                onMouseEnter={() => triggerReaction(false)}
                onClick={() => triggerReaction(true)}
                title="Click or hover the creepy spider!"
            >
                {/* Silk Web Thread */}
                <svg width="2" height={dropped ? "48" : "20"} className="transition-all duration-700 overflow-visible">
                    <line
                        x1="1"
                        y1="0"
                        x2="1"
                        y2={dropped ? "48" : "20"}
                        stroke="rgba(255, 255, 255, 0.45)"
                        strokeWidth="1.2"
                        strokeDasharray="3,2"
                    />
                </svg>

                {/* Animated Spider Body */}
                <div className={`relative ${scared ? 'animate-spooky-shake scale-110' : 'animate-spider-sway'} transition-transform`}>
                    <svg width="32" height="32" viewBox="0 0 100 100" className="drop-shadow-[0_4px_12px_rgba(249,115,22,0.5)]">
                        {/* 8 Creepy Jointed Legs */}
                        <path d="M45 45 Q 20 20 10 35 Q 5 45 2 55" stroke="#1c1917" strokeWidth="4" strokeLinecap="round" fill="none" className={scared ? 'animate-pulse' : ''} />
                        <path d="M45 50 Q 15 35 5 50 Q 2 62 0 75" stroke="#1c1917" strokeWidth="4" strokeLinecap="round" fill="none" className={scared ? 'animate-pulse' : ''} />
                        <path d="M45 55 Q 20 60 12 72 Q 8 82 5 95" stroke="#1c1917" strokeWidth="4" strokeLinecap="round" fill="none" />
                        <path d="M45 60 Q 25 75 18 85 Q 15 95 12 105" stroke="#1c1917" strokeWidth="3.5" strokeLinecap="round" fill="none" />

                        <path d="M55 45 Q 80 20 90 35 Q 95 45 98 55" stroke="#1c1917" strokeWidth="4" strokeLinecap="round" fill="none" className={scared ? 'animate-pulse' : ''} />
                        <path d="M55 50 Q 85 35 95 50 Q 98 62 100 75" stroke="#1c1917" strokeWidth="4" strokeLinecap="round" fill="none" className={scared ? 'animate-pulse' : ''} />
                        <path d="M55 55 Q 80 60 88 72 Q 92 82 95 95" stroke="#1c1917" strokeWidth="4" strokeLinecap="round" fill="none" />
                        <path d="M55 60 Q 75 75 82 85 Q 85 95 88 105" stroke="#1c1917" strokeWidth="3.5" strokeLinecap="round" fill="none" />

                        {/* Abdomen (Rear Body) */}
                        <ellipse cx="50" cy="65" rx="18" ry="22" fill="#18181b" stroke="#3f3f46" strokeWidth="2" />
                        <polygon points="50,56 44,66 56,66" fill="#dc2626" opacity="0.9" />
                        <polygon points="50,76 44,66 56,66" fill="#ea580c" opacity="0.9" />

                        {/* Cephalothorax (Front Body) */}
                        <circle cx="50" cy="45" r="13" fill="#09090b" stroke="#3f3f46" strokeWidth="2" />

                        {/* Glowing Red/Yellow Eyes */}
                        <circle cx="45" cy="40" r="2.5" fill="#ef4444" className="animate-pulse" />
                        <circle cx="55" cy="40" r="2.5" fill="#ef4444" className="animate-pulse" />
                        <circle cx="42" cy="46" r="1.5" fill="#f59e0b" />
                        <circle cx="47" cy="47" r="1.5" fill="#f59e0b" />
                        <circle cx="53" cy="47" r="1.5" fill="#f59e0b" />
                        <circle cx="58" cy="46" r="1.5" fill="#f59e0b" />

                        {/* Poison Fangs */}
                        <path d="M46 56 L 43 62 L 48 58" fill="#e4e4e7" />
                        <path d="M54 56 L 57 62 L 52 58" fill="#e4e4e7" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default DanglingSpider;
